// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
import { type AskPunttReviewComment } from "@mg/schemas/src/christo/askPunttReview";
import { CaretDown } from "@phosphor-icons/react";
import { Button } from "@radix-ui/themes";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { resolveEmbeddedCitationView } from "./resolveEmbeddedCitationView";

import {
  AssetViewerPane,
  type AssetFile,
  type AssetViewerPaneHandle,
} from "../../routes/ask-puntt/components/AssetViewerPane";
import {
  SpreadsheetCitationContextPanel,
  type SpreadsheetCitationSelection,
} from "../../routes/ask-puntt/components/SpreadsheetCitationContextPanel";
import {
  isFileCitation,
  reviewCommentSourceSummaryLabel,
} from "../../routes/ask-puntt/utils/commentSourceUtils";
import {
  isKnowledgeRuleCitation,
  isNavigableFileCitation,
} from "../../utils/citations";
import { KnowledgeRuleCitationPin } from "../KnowledgeRuleCitationDialog";

type AskPunttReviewCommentSourceProps = {
  comment: AskPunttReviewComment;
  contextFiles: readonly AssetFile[];
  reviewFiles?: readonly AssetFile[];
};

function resolveSpreadsheetSource(
  comment: AskPunttReviewComment,
  contextFiles: readonly AssetFile[],
): { kind: "spreadsheet"; citation: SpreadsheetCitationSelection } | null {
  const citations = comment.citations ?? [];
  let sourceUrl: string | undefined;

  for (const citation of citations) {
    if (isFileCitation(citation) && citation.url) {
      sourceUrl = citation.url;
      break;
    }
    if (
      typeof citation === "object" &&
      citation !== null &&
      "sourceUrl" in citation &&
      typeof citation.sourceUrl === "string" &&
      citation.sourceUrl
    ) {
      sourceUrl = citation.sourceUrl;
      break;
    }
  }

  if (!comment.cellAnchor) return null;

  const resolvedUrl =
    sourceUrl ??
    contextFiles.find((file) =>
      citations.some(
        (citation) =>
          isFileCitation(citation) &&
          (citation.url === file.source ||
            citation.originalURL === file.source),
      ),
    )?.source ??
    contextFiles[0]?.source;

  if (!resolvedUrl) return null;

  return {
    kind: "spreadsheet",
    citation: {
      sourceUrl: resolvedUrl,
      cellAnchor: comment.cellAnchor,
      fileName: contextFiles.find((file) => file.source === resolvedUrl)?.name,
    },
  };
}

/**
 * The Knowledge Database document a comment rests on, when the comment cites no
 * file of its own: the first rule citation that names its source.
 *
 * why not "when no file view resolves": every review with a spec carries
 * context files, and the embedded view falls back to the first of them, so a
 * comment resting only on an expert's rule would open the spec instead.
 *
 * why a separate branch and not a context asset: a knowledge source is a
 * private object only its tenant-scoped download signs, and a review file URL
 * built from its key opens nothing (spec kevin.johnson/0019). The rule's own
 * page, pin, box or cell is what the viewer opens at.
 */
function resolveKnowledgeSource(comment: AskPunttReviewComment) {
  const citations = comment.citations ?? [];
  if (citations.some(isNavigableFileCitation)) return null;
  for (const citation of citations) {
    if (isKnowledgeRuleCitation(citation) && citation.sourceId) {
      return { sourceId: citation.sourceId, location: citation.location };
    }
  }
  return null;
}

export function AskPunttReviewCommentSource({
  comment,
  contextFiles,
  reviewFiles = [],
}: AskPunttReviewCommentSourceProps) {
  const { t } = useTranslation("ui");
  const [sourceVisible, setSourceVisible] = useState(false);
  const sourcePaneRef = useRef<AssetViewerPaneHandle>(null);

  const spreadsheetSource = useMemo(
    () => resolveSpreadsheetSource(comment, contextFiles),
    [comment, contextFiles],
  );

  const knowledgeSource = useMemo(
    () => (spreadsheetSource ? null : resolveKnowledgeSource(comment)),
    [comment, spreadsheetSource],
  );

  const embeddedView = useMemo(() => {
    if (spreadsheetSource || knowledgeSource) return null;
    return resolveEmbeddedCitationView(contextFiles, reviewFiles, comment);
  }, [comment, contextFiles, reviewFiles, spreadsheetSource, knowledgeSource]);

  useLayoutEffect(() => {
    if (!sourceVisible || !embeddedView) return;

    let cancelled = false;
    const flush = () => {
      if (cancelled) return;
      if (!sourcePaneRef.current) {
        requestAnimationFrame(flush);
        return;
      }
      sourcePaneRef.current.navigateToFile(
        embeddedView.initialAssetIndex,
        embeddedView.initialViewerPage,
      );
    };
    flush();

    return () => {
      cancelled = true;
    };
  }, [sourceVisible, embeddedView]);

  if (!spreadsheetSource && !embeddedView && !knowledgeSource) return null;

  return (
    <div
      className="mt-3 border-l-2 border-puntt-neutral-gray-5 pl-3"
      data-testid={`ask-v2-review-comment-source-${comment._id}`}
    >
      <p className="text-[11px] font-medium uppercase tracking-wide text-puntt-neutral-gray-9">
        {reviewCommentSourceSummaryLabel(comment)}
        {comment.locationDescription ? (
          <span className="ml-1 normal-case text-puntt-neutral-gray-10">
            {comment.locationDescription}
          </span>
        ) : null}
      </p>
      <Button
        type="button"
        variant="ghost"
        size="1"
        color="gray"
        className="mt-1 !h-auto !p-0 text-[12px] font-medium"
        data-testid={`ask-v2-review-comment-source-toggle-${comment._id}`}
        aria-expanded={sourceVisible}
        onClick={() => setSourceVisible((current) => !current)}
      >
        <CaretDown
          size={12}
          className={`transition-transform ${sourceVisible ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
        {sourceVisible
          ? t("askV2.review.hideSource")
          : t("askV2.review.showSource")}
      </Button>
      {sourceVisible && knowledgeSource ? (
        // why scrollable and taller than the file preview: the document
        // viewer below scrolls itself to the rule, and a clipped 220px box
        // would hide the pin it scrolled to.
        <div
          className="mt-2 max-h-[420px] overflow-auto rounded-md border border-puntt-neutral-gray-5 bg-base-white"
          data-testid={`ask-v2-review-comment-source-preview-${comment._id}`}
        >
          <KnowledgeRuleCitationPin
            sourceId={knowledgeSource.sourceId}
            pageNumber={knowledgeSource.location?.pageNumber}
            bbox={knowledgeSource.location?.bbox}
            relX={knowledgeSource.location?.rel_x}
            relY={knowledgeSource.location?.rel_y}
            cellAnchor={knowledgeSource.location?.cellAnchor}
            testIdPrefix={`ask-v2-review-comment-knowledge-source-${comment._id}`}
            sheetClassName="h-[400px] min-h-0"
          />
        </div>
      ) : null}
      {sourceVisible && !knowledgeSource ? (
        <div
          className="mt-2 max-h-[220px] overflow-hidden rounded-md border border-puntt-neutral-gray-5 bg-base-white"
          data-testid={`ask-v2-review-comment-source-preview-${comment._id}`}
        >
          {spreadsheetSource ? (
            <SpreadsheetCitationContextPanel
              citation={spreadsheetSource.citation}
            />
          ) : embeddedView ? (
            <div className="h-[220px]">
              <AssetViewerPane
                key={`${comment._id}-${embeddedView.initialAssetIndex}-${embeddedView.initialViewerPage}`}
                ref={sourcePaneRef}
                title=""
                assets={embeddedView.sourceAssets}
                hideCommentPanel
                viewerToolbarPlacement="header"
                viewerContentInset="edge"
                initialAssetIndex={embeddedView.initialAssetIndex}
                initialViewerPage={embeddedView.initialViewerPage}
                highlightPosition={
                  sourceVisible ? embeddedView.highlightPosition : null
                }
                citationFocusPin={
                  sourceVisible ? embeddedView.citationFocusPin : null
                }
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
